import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, Globe2, ImagePlus, Send, Flame, Trophy,
  Users, ChevronLeft, Zap, Star, Loader2, X,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveImg(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL || ''}${url}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'just now';
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function Quest() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [countries, setCountries] = useState([]);

  const loadQuest = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quest/today');
      setQuest(data);
      if (data.submitted) {
        const rRes = await api.get('/quest/responses?limit=80');
        setResponses(rRes.data);
        const cRes = await api.get('/quest/countries').catch(() => ({ data: [] }));
        setCountries(cRes.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load today’s quest');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQuest(); }, [loadQuest]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/chat', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImageUrl(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!text.trim() && !imageUrl) {
      toast.error('Share a photo or write a short answer first');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/quest/submit', { text: text.trim(), image_url: imageUrl });
      if (!data.already) {
        toast.success(
          `+${data.reward.xp} XP · +${data.reward.stars}★`,
          {
            description: data.reward.streak_bonus
              ? `Streak bonus! ${data.streak.current}-day streak 🔥`
              : `${data.streak.current}-day streak`,
            duration: 6000,
          },
        );
      }
      refresh?.();
      loadQuest();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/60" data-testid="quest-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading today’s quest…
      </div>
    );
  }
  if (!quest) return null;

  const streak = quest.streak || { current: 0, longest: 0 };
  const filtered = countryFilter ? responses.filter(r => r.country === countryFilter) : responses;

  return (
    <div className="space-y-6" data-testid="quest-page">
      {/* Top bar */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => nav(-1)}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
          data-testid="quest-back"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-semibold flex items-center gap-1">
            <Globe2 size={11} /> Today’s World Quest
          </div>
          <div className="text-white/50 text-xs">One quest. Every country. Every 24 hours.</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <StreakChip streak={streak} />
        </div>
      </div>

      {/* Hero Quest Card */}
      <div
        className="relative overflow-hidden rounded-[28px] border border-white/12 p-6 sm:p-8"
        style={{
          background: 'radial-gradient(circle at 20% 0%, rgba(123,47,247,0.35), transparent 55%), radial-gradient(circle at 90% 100%, rgba(0,198,255,0.28), transparent 60%), #0F0F1B',
        }}
        data-testid="quest-hero"
      >
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 flex items-center gap-1.5">
          <Sparkles size={11} className="text-fuchsia-300" />
          {quest.category} · {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
        </div>
        <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight">
          <span className="mr-2 text-4xl sm:text-5xl">{quest.emoji}</span>
          <span className="align-middle">{quest.prompt}</span>
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/70">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/6 border border-white/10">
            <Users size={12} className="text-cyan-300" />
            {quest.total_responses.toLocaleString()} travelers answered
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-200">
            <Zap size={12} fill="currentColor" /> +{quest.reward_xp} XP
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-400/15 border border-fuchsia-400/30 text-fuchsia-200">
            <Star size={12} fill="currentColor" /> +{quest.reward_stars}
          </span>
        </div>

        {!quest.submitted ? (
          <div className="mt-6 rounded-2xl bg-black/25 border border-white/10 p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/60 mb-2">Your answer</div>
            <Textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Type your answer… photos welcome."
              maxLength={500}
              className="bg-white/5 border-white/10 text-white min-h-[90px] focus:ring-cyan-400/60"
              data-testid="quest-text-input"
            />
            {imageUrl && (
              <div className="mt-3 relative inline-block">
                <img
                  src={resolveImg(imageUrl)}
                  alt="attachment"
                  className="max-h-40 rounded-xl border border-white/15"
                />
                <button
                  onClick={() => setImageUrl(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white hover:bg-black"
                  data-testid="quest-image-clear"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <label
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full cursor-pointer transition border ${uploading ? 'bg-white/5 border-white/10 text-white/40' : 'bg-white/8 border-white/15 hover:bg-white/12 text-white'}`}
                data-testid="quest-image-pick"
              >
                {uploading ? <Loader2 className="animate-spin" size={12} /> : <ImagePlus size={12} />}
                {uploading ? 'Uploading…' : (imageUrl ? 'Change photo' : 'Add photo')}
                <input type="file" accept="image/*" className="hidden" onChange={pickImage} disabled={uploading} />
              </label>
              <Button
                onClick={submit}
                disabled={submitting || (!text.trim() && !imageUrl)}
                data-testid="quest-submit-btn"
                className="ml-auto bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
                Post · Unlock the world
              </Button>
            </div>
            <div className="mt-2 text-[10px] text-white/40">You’ll see everyone’s answers once you post yours.</div>
          </div>
        ) : (
          <div
            className="mt-6 rounded-2xl bg-cyan-400/10 border border-cyan-400/25 p-4 flex items-center gap-3"
            data-testid="quest-already-submitted"
          >
            <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center shrink-0">
              <Trophy size={18} className="text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                You’re in for today 🌍
                {streak.current > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-orange-300 text-xs font-normal">
                    <Flame size={11} /> {streak.current}-day streak
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/60 mt-0.5" data-testid="quest-streak-nudge">
                Answer tomorrow to keep it alive — every <b className="text-orange-300">7 days</b> unlocks a <b className="text-amber-300">+{100}★ bonus</b> and a Voyager Streak badge.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Responses feed (only when submitted) */}
      {quest.submitted && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-wider text-white/60 font-semibold flex items-center gap-1">
              <Globe2 size={12} className="text-cyan-300" /> World feed
            </div>
            <span className="text-white/40 text-xs">
              {filtered.length}
              {countryFilter ? ` from ${countryFilter}` : ` from ${countries.length || 0} countries`}
            </span>
          </div>

          {countries.length > 0 && (
            <div className="-mx-4 px-4 overflow-x-auto no-scrollbar">
              <div className="flex gap-2 min-w-max py-1">
                <CountryChip
                  flag="🌍"
                  name="All"
                  count={responses.length}
                  active={!countryFilter}
                  onClick={() => setCountryFilter('')}
                />
                {countries.map(c => (
                  <CountryChip
                    key={c.country}
                    flag={c.flag}
                    name={c.country}
                    count={c.count}
                    active={countryFilter === c.country}
                    onClick={() => setCountryFilter(c.country === countryFilter ? '' : c.country)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map(r => (
              <ResponseCard key={r.id} r={r} amMe={r.user_id === user?.user_id} />
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center py-10 text-white/40 text-sm">
                No answers yet from that country. Be the first ✨
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StreakChip({ streak }) {
  const cur = streak?.current || 0;
  const hot = cur >= 3;
  const untilBonus = cur > 0 ? (7 - (cur % 7 || 7)) : 7;
  const hint = cur === 0
    ? 'Start a streak today · +100★ every 7 days'
    : untilBonus === 0
      ? '7-day bonus unlocked! Keep going ✨'
      : `${untilBonus} more day${untilBonus === 1 ? '' : 's'} to next 7-day bonus`;
  return (
    <div
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${hot ? 'bg-orange-500/15 border-orange-400/40 text-orange-200' : 'bg-white/5 border-white/10 text-white/70'}`}
      data-testid="quest-streak-chip"
      title={`Longest streak: ${streak?.longest || 0} days · ${hint}`}
    >
      <Flame size={12} className={hot ? 'text-orange-300' : 'text-white/50'} />
      {cur}-day streak
    </div>
  );
}

function CountryChip({ flag, name, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`quest-country-chip-${name}`}
      className={`inline-flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition border ${active ? 'bg-cyan-400/25 border-cyan-400/60 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'}`}
    >
      <span className="text-sm">{flag || '🌐'}</span>
      {name}
      <span className="text-white/40 text-[10px] font-normal">{count}</span>
    </button>
  );
}

function ResponseCard({ r, amMe }) {
  return (
    <div
      className="rounded-2xl border border-white/8 bg-[#141420] hover:border-white/15 transition overflow-hidden"
      data-testid={`quest-response-${r.id}`}
    >
      <div className="p-3 flex items-start gap-3">
        <Link to={`/app/users/${r.user_id}`} className="shrink-0">
          <Avatar className="w-10 h-10 ring-2 ring-white/10">
            <AvatarImage src={r.avatar} />
            <AvatarFallback className="bg-fuchsia-500/30 text-xs">
              {r.displayName?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm">
            <Link
              to={`/app/users/${r.user_id}`}
              className="font-semibold truncate hover:text-cyan-300 transition"
            >
              {r.displayName}
            </Link>
            {r.flag && <span className="text-sm">{r.flag}</span>}
            {amMe && <span className="text-[9px] uppercase tracking-wider text-cyan-300 font-semibold">You</span>}
            <span className="ml-auto text-[10px] text-white/40">{timeAgo(r.created_at)}</span>
          </div>
          {r.text && (
            <div className="mt-1 text-sm text-white/85 whitespace-pre-wrap break-words leading-snug">
              {r.text}
            </div>
          )}
        </div>
      </div>
      {r.image_url && (
        <img
          src={resolveImg(r.image_url)}
          alt="response"
          loading="lazy"
          decoding="async"
          className="w-full max-h-96 object-cover bg-black/30"
        />
      )}
    </div>
  );
}
